import type { CRPlayerCard, PublicCard } from '@/types/qbot';

/**
 * Saved decks come back as bare card-name strings — QBot stores whatever the
 * client POSTed (`decks.ts` persists the raw trimmed string, it only *validates*
 * case-insensitively), so a deck saved as "hog rider" reads back as "hog rider",
 * not "Hog Rider". Every lookup here is therefore case-folded.
 *
 * The catalog behind it (`GET /cr/cards`, public, cached 24h) is what turns
 * those strings back into cards we can draw.
 */
export type CardIndex = Map<string, PublicCard>;

export function buildCardIndex(cards: PublicCard[] | undefined): CardIndex {
  const index: CardIndex = new Map();
  for (const c of cards ?? []) index.set(c.name.toLowerCase(), c);
  return index;
}

/**
 * A catalog card in the shape <DeckGrid> draws.
 *
 * `level`/`maxLevel` are the catalog's, not a player's — a saved deck records
 * cards, never levels. Callers pass `showLevels={false}`; the fields exist only
 * because CRPlayerCard requires them.
 */
function toDeckCard(c: PublicCard): CRPlayerCard {
  return {
    id: c.id,
    name: c.name,
    level: c.maxLevel,
    maxLevel: c.maxLevel,
    count: 0,
    // Only pass a URL that is actually a URL. <CardIcon> falls back to the qbot
    // icon proxy on a *nullish* iconUrl, so an empty string would slip through
    // as a real src and render a broken image instead of the fallback.
    iconUrls: {
      ...(c.iconUrl ? { medium: c.iconUrl } : {}),
      ...(c.evolutionIconUrl ? { evolutionMedium: c.evolutionIconUrl } : {}),
    } as CRPlayerCard['iconUrls'],
    elixirCost: c.elixir,
  };
}

export interface ResolvedDeck {
  cards: CRPlayerCard[];
  /** Names the catalog didn't know — rendered as text chips, not dropped. */
  unresolved: string[];
  /** Mean elixir over the resolved cards, or undefined if none resolved. */
  avgElixir?: number;
}

/**
 * Resolve saved-deck card names against the catalog.
 *
 * Partial by design: an unknown name yields a text chip for that one card
 * rather than collapsing the whole deck. Import is offered only when all eight
 * resolve — a 7-card copyDeck link is a deck the game refuses.
 */
export function resolveDeckNames(names: string[], index: CardIndex): ResolvedDeck {
  const cards: CRPlayerCard[] = [];
  const unresolved: string[] = [];

  for (const name of names) {
    const hit = index.get(name.toLowerCase());
    if (hit) cards.push(toDeckCard(hit));
    else unresolved.push(name);
  }

  const withElixir = cards.filter((c) => typeof c.elixirCost === 'number');
  const avgElixir = withElixir.length
    ? withElixir.reduce((sum, c) => sum + (c.elixirCost ?? 0), 0) / withElixir.length
    : undefined;

  return { cards, unresolved, avgElixir };
}
